import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../services/api';

interface UseFlowExecutionResult {
  execution: any | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useFlowExecution(executionId: string | undefined): UseFlowExecutionResult {
  const [execution, setExecution] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollCountRef = useRef(0);
  // Anzahl aufeinanderfolgender 429-Responses — steuert den Backoff-Faktor
  const rateLimitHitsRef = useRef(0);

  const clearCurrentInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Gibt das gewünschte Polling-Intervall in ms zurück
  const getInterval = useCallback(() => {
    const count = pollCountRef.current;
    // Basis-Intervall: 1s → 3s → 5s (adaptiv nach Anzahl der Polls)
    let base: number;
    if (count < 10) base = 1000;
    else if (count < 30) base = 3000;
    else base = 5000;

    // Bei Rate-Limit-Hits: exponentieller Backoff (max 30s)
    const hits = rateLimitHitsRef.current;
    if (hits > 0) {
      const backoff = Math.min(base * Math.pow(2, hits), 30000);
      return backoff;
    }
    return base;
  }, []);

  const scheduleNextPoll = useCallback((fetchFn: () => void) => {
    clearCurrentInterval();
    intervalRef.current = setInterval(() => {
      pollCountRef.current++;
      fetchFn();
    }, getInterval());
  }, [clearCurrentInterval, getInterval]);

  const fetchExecution = useCallback(async () => {
    if (!executionId) return;
    try {
      const res = await api.flows.getExecutionById(executionId);
      setExecution(res.data);
      setError(null);
      // Erfolgreicher Request — Rate-Limit-Backoff zurücksetzen
      rateLimitHitsRef.current = 0;

      // Adaptives Polling: Stoppe bei Terminal-Status
      const status = res.data?.status;
      if (status === 'completed' || status === 'failed' || status === 'cancelled') {
        clearCurrentInterval();
        return;
      }

      // Intervall nach jedem erfolgreichen Poll anpassen
      scheduleNextPoll(fetchExecution);
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status;
      if (status === 429) {
        // Rate-Limit getroffen — kein Error-State, Backoff erhöhen und weiterpolling
        rateLimitHitsRef.current++;
        scheduleNextPoll(fetchExecution);
      } else {
        setError(err.message || 'Fehler beim Laden der Execution');
      }
    } finally {
      setLoading(false);
    }
  // fetchExecution hängt von scheduleNextPoll ab, aber scheduleNextPoll hängt nicht
  // von fetchExecution ab — Abhängigkeit wird über Callback-Referenz aufgelöst
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionId, clearCurrentInterval, scheduleNextPoll]);

  useEffect(() => {
    if (!executionId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    pollCountRef.current = 0;
    rateLimitHitsRef.current = 0;
    fetchExecution();

    return () => {
      clearCurrentInterval();
    };
  }, [executionId, fetchExecution, clearCurrentInterval]);

  return { execution, loading, error, refetch: fetchExecution };
}
